<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LiaAgentsAPI from 'dashboard/api/liaAgents';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  companyName: {
    type: String,
    default: '',
  },
  agent: {
    type: Object,
    default: null,
  },
  workflowAgentsConfig: {
    type: Object,
    default: () => ({
      triagem_name: 'Lia',
      vendas_name: 'Anna',
      financeiro_name: 'Valentina',
      suporte_name: 'Bianca',
    }),
  },
  behaviorConfig: {
    type: Object,
    default: () => ({}),
  },
  companyDataConfig: {
    type: Object,
    default: () => ({}),
  },
  personaConfig: {
    type: Object,
    default: () => ({}),
  },
  agentPromptsConfig: {
    type: Object,
    default: () => ({
      triagem: '',
      vendas: '',
      financeiro: '',
      suporte: '',
    }),
  },
});

const emit = defineEmits(['update:modelValue', 'update:agentPromptsConfig']);
const { t } = useI18n();

// Save state
const isSaving = ref(false);
const saveMessage = ref('');
const saveSuccess = ref(false);

// Version helpers
const agentVersion = computed(() => props.agent?.version || '1.4');
const isLia31 = computed(() => {
  const version = props.agent?.lia_version || '';
  return version.includes('3.1');
});

// Get ERP provider name
const erpProviderName = computed(() => {
  const provider = props.agent?.integration_provider;
  const names = {
    ixc: 'IXC',
    mycore: 'MyCore',
    sgp: 'SGP',
    voalle: 'Voalle',
    hubsoft: 'HubSoft',
    mk_solutions: 'MK Solutions',
  };
  return names[provider] || provider?.toUpperCase() || 'ERP';
});

// Active agent tab for LIA 3.1
const activeAgentTab = ref('triagem');

// Agent tabs configuration - 4 tabs: Triagem, Vendas, Financeiro, Suporte
const agentTabs = computed(() => {
  return [
    {
      id: 'triagem',
      name: props.workflowAgentsConfig.triagem_name || 'Lia',
      role: 'Triagem',
      icon: 'i-lucide-scan-search',
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
      description: 'Acolhimento inicial e direcionamento para o agente correto',
    },
    {
      id: 'vendas',
      name: props.workflowAgentsConfig.vendas_name || 'Anna',
      role: 'Vendas',
      icon: 'i-lucide-shopping-bag',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      description: 'Vendas consultivas e apresentação de planos',
    },
    {
      id: 'financeiro',
      name: props.workflowAgentsConfig.financeiro_name || 'Valentina',
      role: 'Financeiro',
      icon: 'i-lucide-wallet',
      color: 'text-n-amber-11',
      bgColor: 'bg-n-amber-3',
      description: 'Faturas, segunda via, desbloqueio de serviços',
    },
    {
      id: 'suporte',
      name: props.workflowAgentsConfig.suporte_name || 'Bianca',
      role: 'Suporte',
      icon: 'i-lucide-wrench',
      color: 'text-n-blue-11',
      bgColor: 'bg-n-blue-3',
      description: 'Problemas técnicos, conexão, diagnóstico',
    },
  ];
});

// Get current agent tab
const currentAgentTab = computed(() => {
  return agentTabs.value.find(tab => tab.id === activeAgentTab.value);
});

// Get/set current agent prompt
const currentAgentPrompt = computed({
  get: () => props.agentPromptsConfig[activeAgentTab.value] || '',
  set: val => {
    emit('update:agentPromptsConfig', {
      ...props.agentPromptsConfig,
      [activeAgentTab.value]: val,
    });
  },
});

// Check if current prompt is empty
const isCurrentPromptEmpty = computed(
  () => !currentAgentPrompt.value || currentAgentPrompt.value.trim() === ''
);

// Legacy single prompt (for LIA 1.4/2.1)
const singlePrompt = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
});

const isSinglePromptEmpty = computed(
  () => !singlePrompt.value || singlePrompt.value.trim() === ''
);

// savePrompts is defined after isOverLimit/hardLimit/charCount (below)

// AI Assistant state
const showAssistant = ref(false);
const assistantInput = ref('');
const assistantMessages = ref([]);
const isAssistantLoading = ref(false);
const messagesContainer = ref(null);
const isGenerating = ref(false);

// Build behavior instructions from config
const buildBehaviorInstructions = () => {
  const behavior = props.behaviorConfig;
  const instructions = [];

  if (behavior.emoji_usage === 'never') {
    instructions.push('NUNCA use emojis em suas respostas.');
  } else if (behavior.emoji_usage === 'sometimes') {
    instructions.push(
      'Use emojis com moderação, apenas em saudações e despedidas.'
    );
  } else if (behavior.emoji_usage === 'frequently') {
    instructions.push(
      'Use emojis para tornar a conversa mais leve e amigável.'
    );
  }

  if (behavior.formality === 'formal') {
    instructions.push(
      'Use linguagem formal. Trate o cliente por Senhor/Senhora.'
    );
  } else if (behavior.formality === 'casual') {
    instructions.push('Use linguagem casual e amigável.');
  } else if (behavior.formality === 'friendly') {
    instructions.push(
      'Seja super descontraído, como se estivesse conversando com um amigo.'
    );
  }

  if (behavior.response_length === 'short') {
    instructions.push(
      'Seja extremamente conciso. Respostas devem ter no máximo 2-3 frases.'
    );
  } else if (behavior.response_length === 'detailed') {
    instructions.push('Forneça respostas detalhadas e completas.');
  }

  if (behavior.tone === 'direct') {
    instructions.push('Seja direto e objetivo.');
  } else if (behavior.tone === 'enthusiastic') {
    instructions.push('Seja entusiasmado e positivo.');
  }

  return instructions.join('\n');
};

// Build company info section
const buildCompanyInfo = () => {
  const company = props.companyDataConfig;
  const info = [];

  if (company.name) info.push(`- Nome: ${company.name}`);
  if (company.phone) info.push(`- Telefone: ${company.phone}`);
  if (company.whatsapp) info.push(`- WhatsApp: ${company.whatsapp}`);
  if (company.email) info.push(`- Email: ${company.email}`);
  if (company.website) info.push(`- Site: ${company.website}`);
  if (company.address) info.push(`- Endereço: ${company.address}`);
  if (company.business_hours) info.push(`- Horário: ${company.business_hours}`);

  return info.length > 0 ? info.join('\n') : '';
};

// Generate Triagem prompt
const generateTriagemPrompt = (
  agentConfig,
  behaviorInstructions,
  companyInfo
) => {
  const companyName =
    props.companyName || props.companyDataConfig?.name || '{{company_name}}';
  const vendasName = props.workflowAgentsConfig.vendas_name || 'Anna';
  const financeiroName =
    props.workflowAgentsConfig.financeiro_name || 'Valentina';
  const suporteName = props.workflowAgentsConfig.suporte_name || 'Bianca';

  let prompt = `## IDENTIDADE
Você é ${agentConfig.name}, assistente virtual da ${companyName}.

Sua missão é fazer o primeiro contato com o cliente, identificar sua necessidade e direcioná-lo para o agente correto:
- Se o cliente quer contratar ou conhecer planos → transfere para ${vendasName} (Vendas)
- Se o cliente precisa de fatura/pagamento → transfere para ${financeiroName} (Financeiro)
- Se o cliente tem problemas técnicos → transfere para ${suporteName} (Suporte)

## CONTEXTO
- A conversa se passa no WhatsApp
- Você está integrado com o sistema ${erpProviderName.value} para consultas em tempo real
- O nome do cliente está disponível no perfil`;

  if (companyInfo) {
    prompt += `

<informacoes_empresa>
${companyInfo}
</informacoes_empresa>`;
  }

  prompt += `

## COMPORTAMENTO
${behaviorInstructions || '- Seja acolhedor e eficiente\n- Identifique rapidamente a necessidade do cliente'}

## RESTRIÇÕES
- JAMAIS invente informações
- Não tente resolver problemas por conta própria, encaminhe para o agente correto
- Não faça mais de uma pergunta por vez`;

  return prompt;
};

// Generate Vendas prompt
const generateVendasPrompt = (
  agentConfig,
  behaviorInstructions,
  companyInfo
) => {
  const companyName =
    props.companyName || props.companyDataConfig?.name || '{{company_name}}';
  const financeiroName =
    props.workflowAgentsConfig.financeiro_name || 'Valentina';
  const suporteName = props.workflowAgentsConfig.suporte_name || 'Bianca';

  let prompt = `## IDENTIDADE
Você é ${agentConfig.name}, consultora especialista da ${companyName}.
Sua missão é conduzir vendas consultivas de alta conversão, descobrindo necessidades reais.

## CONTEXTO
- A conversa se passa no WhatsApp
- Você está integrado com o sistema ${erpProviderName.value} para consultas em tempo real
- No início da conversa, SEMPRE peça pelo CPF do cliente
- O nome do cliente está disponível no perfil`;

  if (companyInfo) {
    prompt += `

<informacoes_empresa>
${companyInfo}
</informacoes_empresa>`;
  }

  prompt += `

## FLUXO DE VENDAS
1. **Qualificação**: Peça o CEP e verifique a viabilidade
2. **Descoberta**: Pergunte quantas pessoas usam internet, se assistem streaming
3. **Apresentação**: Apresente UM produto por vez, com pergunta de engajamento
4. **Fechamento**: Colete CPF e transfira para o comercial finalizar

## FERRAMENTAS
- \`consultar_dados_cep\`: Busca dados do endereço pelo CEP
- \`consultar_viabilidade\`: Verifica cobertura na região
- \`produtos\`: Retorna produtos disponíveis
- \`consultar_dados_cpf\`: Consulta dados e score do CPF
- \`transferir_comercial\`: Transfere para o time comercial
- \`transferir_financeiro\`: Transfere para ${financeiroName} (Financeiro)
- \`transferir_suporte\`: Transfere para ${suporteName} (Suporte)

## COMPORTAMENTO
${behaviorInstructions || '- Seja entusiasmado mas não insistente\n- Foque nas necessidades do cliente'}

## RESTRIÇÕES
- JAMAIS invente informações
- JAMAIS ofereça enviar segunda via por email, apenas WhatsApp
- Não faça mais de uma pergunta por vez
- Sempre confirme o CPF antes de transferir para outro agente`;

  return prompt;
};

// Generate Financeiro prompt
const generateFinanceiroPrompt = (
  agentConfig,
  behaviorInstructions,
  companyInfo
) => {
  const companyName =
    props.companyName || props.companyDataConfig?.name || '{{company_name}}';

  let prompt = `## IDENTIDADE
Você é ${agentConfig.name}, da equipe financeira da ${companyName}.

Sua função é resolver problemas de fatura e desbloqueio com máxima eficiência e simpatia.

## CONTEXTO
- A conversa se passa no WhatsApp
- Você está integrado com o sistema ${erpProviderName.value} para consultas em tempo real
- Boleto = fatura = segunda via (são a mesma coisa)
- Se o cliente precisar de outro setor (SUPORTE ou VENDAS), transfira-o

## TAREFA
- No início, SEMPRE peça pelo CPF do cliente
- Resolva problemas de faturas, atrasos, bloqueio/desbloqueio
- Quando receber comprovante de pagamento, informe sobre compensação (PIX = na hora, Boleto = dia útil seguinte)`;

  if (companyInfo) {
    prompt += `

<informacoes_empresa>
${companyInfo}
</informacoes_empresa>`;
  }

  prompt += `

## FERRAMENTAS
- \`segunda_via\`: Busca faturas do cliente pelo CPF
- \`desbloqueio\`: Desbloqueia serviços do cliente
- \`buscar_contratos\`: Busca contratos do cliente
- \`transferir_financeiro\`: Transfere para a equipe financeira humana

## COMPORTAMENTO
${behaviorInstructions || '- Seja amigável e eficiente\n- Resolva rapidamente os problemas'}

## RESTRIÇÕES
- JAMAIS invente informações
- JAMAIS ofereça enviar segunda via por email, apenas WhatsApp
- JAMAIS transfira sem saber o CPF do cliente`;

  return prompt;
};

// Generate Suporte prompt
const generateSuportePrompt = (
  agentConfig,
  behaviorInstructions,
  companyInfo
) => {
  const companyName =
    props.companyName || props.companyDataConfig?.name || '{{company_name}}';

  let prompt = `## IDENTIDADE
Você é ${agentConfig.name}, do time de suporte técnico da ${companyName}.

Você é carismática, prestativa e direta. Sua comunicação é levemente informal.

## CONTEXTO
- A conversa se passa no WhatsApp
- Você está integrado com o sistema ${erpProviderName.value} para consultas em tempo real
- O cliente já é nosso cliente (foi transferido de outro agente)
- Se precisar de outro setor (FINANCEIRO ou VENDAS), transfira-o

## TAREFA
- No início, SEMPRE peça pelo CPF do cliente
- Resolva problemas técnicos comuns (lentidão, conexão caindo, sem internet)
- Faça no máximo 3 perguntas durante a conversa
- Se não conseguir resolver, transfira para o suporte humano`;

  if (companyInfo) {
    prompt += `

<informacoes_empresa>
${companyInfo}
</informacoes_empresa>`;
  }

  prompt += `

<problemas_comuns>
#### Internet caindo constantemente
- Pergunte se usa Wi-Fi ou cabo
- Sugira reiniciar o roteador (desligar 30 segundos)

#### Internet lenta
- Pergunte se é o tempo todo ou em horários específicos
- Peça para se aproximar do roteador

#### Sem conexão
- Verifique se as luzes do roteador estão acesas
- Oriente verificar tomada/fonte

#### Cliente irritado
- Transfira imediatamente para o suporte humano

#### Alterar senha Wi-Fi
- Só é possível com técnico, transfira para o suporte
</problemas_comuns>

## FERRAMENTAS
- \`verificar_pendencias\`: Verifica se há bloqueio por pendência
- \`transferir_suporte\`: Transfere para a equipe de suporte humana

## COMPORTAMENTO
${behaviorInstructions || '- Seja paciente e didático\n- Use linguagem simples'}

## RESTRIÇÕES
- JAMAIS invente informações
- JAMAIS ofereça enviar segunda via por email, apenas WhatsApp
- JAMAIS concorde com caracterizações negativas do serviço`;

  return prompt;
};

// Generate basic prompt (LIA 1.4/2.1)
const generateBasicPrompt = (behaviorInstructions, companyInfo) => {
  const companyName =
    props.companyName || props.companyDataConfig?.name || '{{company_name}}';
  const persona = props.personaConfig;

  let prompt = `## IDENTIDADE
Você é ${persona.description || 'um assistente virtual inteligente'} da ${companyName}.`;

  if (persona.role) {
    const roleLabels = {
      atendente: 'Atendente',
      vendedor: 'Vendedor',
      suporte: 'Suporte Técnico',
      financeiro: 'Financeiro',
      recepcionista: 'Recepcionista',
      consultor: 'Consultor',
    };
    prompt += `\nSua função é ${roleLabels[persona.role] || persona.role}.`;
  }

  prompt += `

## CONTEXTO
- A conversa se passa no WhatsApp
- Sempre se apresente no início da conversa`;

  if (companyInfo) {
    prompt += `

<informacoes_empresa>
${companyInfo}
</informacoes_empresa>`;
  }

  if (behaviorInstructions) {
    prompt += `

## COMPORTAMENTO
${behaviorInstructions}`;
  } else {
    prompt += `

## COMPORTAMENTO
- Seja cordial e profissional
- Respostas curtas e objetivas
- Quando não souber algo, admita e ofereça transferir para um humano`;
  }

  prompt += `

## RESTRIÇÕES
- JAMAIS invente informações que não conhece
- Não compartilhe informações confidenciais
- Mantenha sempre um tom profissional`;

  return prompt;
};

// Generate prompt for current agent
const generateAgentPrompt = async () => {
  isGenerating.value = true;
  try {
    const behaviorInstructions = buildBehaviorInstructions();
    const companyInfo = buildCompanyInfo();
    const agentConfig = currentAgentTab.value;

    let generatedPrompt = '';

    switch (activeAgentTab.value) {
      case 'triagem':
        generatedPrompt = generateTriagemPrompt(
          agentConfig,
          behaviorInstructions,
          companyInfo
        );
        break;
      case 'vendas':
        generatedPrompt = generateVendasPrompt(
          agentConfig,
          behaviorInstructions,
          companyInfo
        );
        break;
      case 'financeiro':
        generatedPrompt = generateFinanceiroPrompt(
          agentConfig,
          behaviorInstructions,
          companyInfo
        );
        break;
      case 'suporte':
        generatedPrompt = generateSuportePrompt(
          agentConfig,
          behaviorInstructions,
          companyInfo
        );
        break;
      default:
        generatedPrompt = '';
    }

    currentAgentPrompt.value = generatedPrompt;
  } finally {
    isGenerating.value = false;
  }
};

// Generate single prompt (for LIA 1.4/2.1)
const generateSinglePrompt = async () => {
  isGenerating.value = true;
  try {
    const behaviorInstructions = buildBehaviorInstructions();
    const companyInfo = buildCompanyInfo();
    singlePrompt.value = generateBasicPrompt(behaviorInstructions, companyInfo);
  } finally {
    isGenerating.value = false;
  }
};

// Character limits per version (with 10% hard cap)
const VERSION_CHAR_LIMITS = { 1.0: 4000, 1.4: 6000, 2.1: 16000, 3.1: 16000 };
const charCount = computed(() => {
  if (isLia31.value) {
    return currentAgentPrompt.value?.length || 0;
  }
  return singlePrompt.value?.length || 0;
});
const maxChars = computed(
  () => VERSION_CHAR_LIMITS[agentVersion.value] || 6000
);
const hardLimit = computed(() => Math.floor(maxChars.value * 1.1));
const isOverLimit = computed(() => charCount.value > hardLimit.value);

const savePrompts = async () => {
  if (!props.agent?.id) return;
  if (isOverLimit.value) {
    saveSuccess.value = false;
    const excess = charCount.value - hardLimit.value;
    saveMessage.value = `Prompt excede o limite. Reduza ${excess} caracteres para salvar.`;
    return;
  }
  isSaving.value = true;
  saveMessage.value = '';
  try {
    let response;
    if (isLia31.value) {
      response = await LiaAgentsAPI.savePrompts(
        props.agent.id,
        props.agentPromptsConfig
      );
    } else {
      response = await LiaAgentsAPI.saveSystemPrompt(
        props.agent.id,
        singlePrompt.value
      );
    }
    saveSuccess.value = response.data?.success;
    saveMessage.value = response.data?.workflow_updated
      ? 'Salvo e sincronizado com o fluxo'
      : response.data?.warning || 'Salvo';
    setTimeout(() => {
      saveMessage.value = '';
    }, 4000);
  } catch (error) {
    saveSuccess.value = false;
    saveMessage.value = error.response?.data?.error || 'Erro ao salvar';
    setTimeout(() => {
      saveMessage.value = '';
    }, 4000);
  } finally {
    isSaving.value = false;
  }
};

// Quick suggestions for the AI assistant
const quickSuggestions = computed(() => [
  { id: 'improve', label: t('LIA_AGENTS.PROMPT.ASSISTANT_IMPROVE') },
  { id: 'formal', label: t('LIA_AGENTS.PROMPT.ASSISTANT_MORE_FORMAL') },
  { id: 'friendly', label: t('LIA_AGENTS.PROMPT.ASSISTANT_MORE_FRIENDLY') },
  { id: 'shorter', label: t('LIA_AGENTS.PROMPT.ASSISTANT_SHORTER') },
  { id: 'detailed', label: t('LIA_AGENTS.PROMPT.ASSISTANT_MORE_DETAILED') },
]);

// Toggle assistant panel
const toggleAssistant = () => {
  showAssistant.value = !showAssistant.value;
  if (showAssistant.value && assistantMessages.value.length === 0) {
    assistantMessages.value.push({
      role: 'assistant',
      content: t('LIA_AGENTS.PROMPT.ASSISTANT_WELCOME'),
    });
  }
};

// Send message to assistant
const sendToAssistant = async messageText => {
  const message = messageText || assistantInput.value.trim();
  if (!message || isAssistantLoading.value) return;

  const currentPromptText = isLia31.value
    ? currentAgentPrompt.value
    : singlePrompt.value;

  assistantMessages.value.push({
    role: 'user',
    content: message,
  });
  assistantInput.value = '';
  isAssistantLoading.value = true;

  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }

  try {
    const response = await LiaAgentsAPI.improvePrompt({
      current_prompt: currentPromptText,
      user_request: message,
      company_name: props.companyName,
      lia_version: props.agent?.lia_version,
    });

    if (response.data?.improved_prompt) {
      assistantMessages.value.push({
        role: 'assistant',
        content:
          response.data.explanation ||
          t('LIA_AGENTS.PROMPT.ASSISTANT_IMPROVED'),
        improved_prompt: response.data.improved_prompt,
      });
    } else {
      assistantMessages.value.push({
        role: 'assistant',
        content:
          response.data?.message || t('LIA_AGENTS.PROMPT.ASSISTANT_ERROR'),
      });
    }
  } catch {
    assistantMessages.value.push({
      role: 'assistant',
      content: t('LIA_AGENTS.PROMPT.ASSISTANT_ERROR'),
    });
  } finally {
    isAssistantLoading.value = false;
    await nextTick();
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  }
};

// Apply improved prompt from assistant
const applyImprovedPrompt = improvedPrompt => {
  if (isLia31.value) {
    currentAgentPrompt.value = improvedPrompt;
  } else {
    singlePrompt.value = improvedPrompt;
  }
  assistantMessages.value.push({
    role: 'assistant',
    content: t('LIA_AGENTS.PROMPT.ASSISTANT_APPLIED'),
  });
};

// Handle quick suggestion click
const handleQuickSuggestion = suggestion => {
  const messages = {
    improve: 'Melhore este prompt para torná-lo mais eficaz',
    formal: 'Torne o prompt mais formal e profissional',
    friendly: 'Torne o prompt mais amigável e acolhedor',
    shorter: 'Resuma o prompt mantendo as informações essenciais',
    detailed: 'Adicione mais detalhes e instruções ao prompt',
  };
  sendToAssistant(messages[suggestion.id]);
};

// Clear assistant messages when switching tabs
watch(activeAgentTab, () => {
  assistantMessages.value = [];
  showAssistant.value = false;
});
</script>

<template>
  <div class="space-y-6">
    <!-- LIA 3.1: Multi-agent prompts with tabs -->
    <div v-if="isLia31">
      <!-- Agent Tabs -->
      <div class="bg-n-solid-3 rounded-xl overflow-hidden border border-n-weak">
        <div class="flex border-b border-n-weak overflow-x-auto">
          <button
            v-for="tab in agentTabs"
            :key="tab.id"
            type="button"
            class="flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap"
            :class="
              activeAgentTab === tab.id
                ? 'border-woot-500 text-woot-600 dark:text-woot-400 bg-woot-50/50 dark:bg-woot-900/10'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="activeAgentTab = tab.id"
          >
            <span :class="[tab.icon, tab.color]" class="size-4" />
            <span>{{ tab.name }}</span>
            <span
              v-if="agentPromptsConfig[tab.id]"
              class="w-2 h-2 rounded-full bg-green-500"
              title="Prompt configurado"
            />
          </button>
        </div>

        <!-- Current Agent Header -->
        <div
          class="px-5 py-4 border-b border-n-weak"
          :class="currentAgentTab?.bgColor"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-lg bg-white/50 dark:bg-n-solid-2/50 flex items-center justify-center"
            >
              <span
                :class="[currentAgentTab?.icon, currentAgentTab?.color]"
                class="size-5"
              />
            </div>
            <div>
              <h3 class="font-semibold text-n-slate-12">
                Prompt do {{ currentAgentTab?.name }} ({{
                  currentAgentTab?.role
                }})
              </h3>
              <p class="text-xs text-n-slate-11">
                {{ currentAgentTab?.description }}
              </p>
            </div>
          </div>
        </div>

        <!-- Prompt Content -->
        <div class="p-5">
          <!-- Empty State -->
          <div v-if="isCurrentPromptEmpty" class="text-center py-8">
            <div
              class="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-woot-500 to-purple-500 flex items-center justify-center"
            >
              <span class="i-lucide-wand-2 size-7 text-white" />
            </div>
            <h4 class="text-base font-semibold text-n-slate-12 mb-2">
              Gerar Prompt para {{ currentAgentTab?.name }}
            </h4>
            <p class="text-sm text-n-slate-11 mb-4 max-w-sm mx-auto">
              Clique para gerar o prompt baseado nas configurações de
              comportamento e dados da empresa.
            </p>
            <button
              type="button"
              class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-woot-500 to-purple-500 hover:from-woot-600 hover:to-purple-600 rounded-lg transition-all disabled:opacity-50"
              :disabled="isGenerating"
              @click="generateAgentPrompt"
            >
              <span
                v-if="isGenerating"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span v-else class="i-lucide-sparkles size-4" />
              {{ isGenerating ? 'Gerando...' : 'Gerar Prompt' }}
            </button>
          </div>

          <!-- Prompt Editor -->
          <div v-else class="flex gap-4">
            <div class="flex-1">
              <div class="flex items-center justify-between mb-3">
                <span
                  class="text-xs"
                  :class="
                    isOverLimit
                      ? 'text-red-500 font-semibold'
                      : charCount > maxChars
                        ? 'text-amber-500'
                        : 'text-n-slate-11'
                  "
                >
                  {{ charCount }}/{{ maxChars }}
                </span>
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                    :disabled="isGenerating"
                    @click="generateAgentPrompt"
                  >
                    <span
                      :class="
                        isGenerating
                          ? 'i-lucide-loader-2 animate-spin'
                          : 'i-lucide-refresh-cw'
                      "
                      class="size-3.5"
                    />
                    Regenerar
                  </button>
                  <button
                    type="button"
                    class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors"
                    :class="
                      showAssistant
                        ? 'bg-woot-500 text-white'
                        : 'bg-woot-50 text-woot-600 dark:bg-woot-900/30 dark:text-woot-400 hover:bg-woot-100'
                    "
                    @click="toggleAssistant"
                  >
                    <span class="i-lucide-sparkles size-3.5" />
                    Assistente IA
                  </button>
                  <button
                    type="button"
                    class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                    :disabled="isSaving || isOverLimit"
                    @click="savePrompts"
                  >
                    <span
                      :class="
                        isSaving
                          ? 'i-lucide-loader-2 animate-spin'
                          : 'i-lucide-save'
                      "
                      class="size-3.5"
                    />
                    {{ isSaving ? 'Salvando...' : 'Salvar Prompts' }}
                  </button>
                </div>
                <div
                  v-if="saveMessage"
                  class="flex items-center gap-1.5 text-xs font-medium"
                  :class="saveSuccess ? 'text-green-600' : 'text-red-500'"
                >
                  <span
                    :class="
                      saveSuccess
                        ? 'i-lucide-check-circle'
                        : 'i-lucide-alert-circle'
                    "
                    class="size-3.5"
                  />
                  {{ saveMessage }}
                </div>
              </div>
              <div
                class="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-700 dark:text-amber-400"
              >
                <span class="i-lucide-shield-check size-4 shrink-0" />
                <span>
                  As seções <strong>FERRAMENTAS/TOOLS</strong> e
                  <strong>RESTRIÇÕES</strong> são protegidas e serão adicionadas
                  automaticamente durante o deploy. Edite apenas as seções de
                  identidade, contexto e tarefa.
                </span>
              </div>
              <textarea
                v-model="currentAgentPrompt"
                rows="20"
                :placeholder="`Prompt do agente ${currentAgentTab?.name}...`"
                class="w-full px-4 py-3 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500 font-mono text-sm resize-y min-h-[400px]"
              />
            </div>

            <!-- AI Assistant Panel -->
            <div
              v-if="showAssistant"
              class="w-72 bg-n-solid-2 rounded-xl flex flex-col overflow-hidden border border-n-weak"
            >
              <div
                class="px-3 py-2 border-b border-n-weak bg-gradient-to-r from-woot-500 to-purple-500"
              >
                <div class="flex items-center justify-between">
                  <span class="text-xs font-semibold text-white">Assistente IA</span>
                  <button
                    type="button"
                    class="p-1 text-white/80 hover:text-white"
                    @click="showAssistant = false"
                  >
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </div>
              </div>
              <div class="px-2 py-2 border-b border-n-weak">
                <div class="flex flex-wrap gap-1">
                  <button
                    v-for="suggestion in quickSuggestions"
                    :key="suggestion.id"
                    type="button"
                    class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-n-alpha-2 text-n-slate-11 hover:bg-woot-100 hover:text-woot-600 transition-colors"
                    :disabled="isAssistantLoading"
                    @click="handleQuickSuggestion(suggestion)"
                  >
                    {{ suggestion.label }}
                  </button>
                </div>
              </div>
              <div
                ref="messagesContainer"
                class="flex-1 overflow-y-auto p-2 space-y-2 max-h-[300px]"
              >
                <div
                  v-for="(message, index) in assistantMessages"
                  :key="index"
                  class="flex"
                  :class="
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  "
                >
                  <div
                    class="max-w-[90%] rounded-lg px-2 py-1.5 text-xs"
                    :class="
                      message.role === 'user'
                        ? 'bg-woot-500 text-white'
                        : 'bg-n-alpha-2 text-n-slate-12'
                    "
                  >
                    <p class="whitespace-pre-wrap">{{ message.content }}</p>
                    <button
                      v-if="message.improved_prompt"
                      type="button"
                      class="mt-1.5 w-full flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-medium rounded bg-green-500 text-white hover:bg-green-600"
                      @click="applyImprovedPrompt(message.improved_prompt)"
                    >
                      <span class="i-lucide-check size-3" />
                      Aplicar
                    </button>
                  </div>
                </div>
                <div v-if="isAssistantLoading" class="flex justify-start">
                  <div class="bg-n-alpha-2 rounded-lg px-2 py-1.5">
                    <div class="flex items-center gap-1">
                      <span
                        class="w-1.5 h-1.5 bg-n-slate-11 rounded-full animate-bounce"
                        style="animation-delay: 0ms"
                      />
                      <span
                        class="w-1.5 h-1.5 bg-n-slate-11 rounded-full animate-bounce"
                        style="animation-delay: 150ms"
                      />
                      <span
                        class="w-1.5 h-1.5 bg-n-slate-11 rounded-full animate-bounce"
                        style="animation-delay: 300ms"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="p-2 border-t border-n-weak">
                <div class="flex gap-1.5">
                  <input
                    v-model="assistantInput"
                    type="text"
                    placeholder="Como posso ajudar?"
                    class="flex-1 px-2 py-1.5 text-xs bg-n-alpha-3 border border-n-weak rounded text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-woot-500"
                    :disabled="isAssistantLoading"
                    @keydown.enter="sendToAssistant()"
                  />
                  <button
                    type="button"
                    class="p-1.5 bg-woot-500 text-white rounded hover:bg-woot-600 disabled:opacity-50"
                    :disabled="!assistantInput.trim() || isAssistantLoading"
                    @click="sendToAssistant()"
                  >
                    <span class="i-lucide-send size-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- LIA 1.4/2.1: Single prompt -->
    <div v-else>
      <!-- Empty State -->
      <div v-if="isSinglePromptEmpty" class="bg-n-solid-3 rounded-xl p-8">
        <div class="text-center max-w-md mx-auto">
          <div
            class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-woot-500 to-purple-500 flex items-center justify-center"
          >
            <span class="i-lucide-wand-2 size-8 text-white" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
            Gerar Prompt do Sistema
          </h3>
          <p class="text-sm text-n-slate-11 mb-6">
            Clique para gerar automaticamente o prompt baseado nas
            configurações.
          </p>
          <button
            type="button"
            class="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-woot-500 to-purple-500 hover:from-woot-600 hover:to-purple-600 rounded-lg transition-all disabled:opacity-50"
            :disabled="isGenerating"
            @click="generateSinglePrompt"
          >
            <span
              v-if="isGenerating"
              class="i-lucide-loader-2 size-5 animate-spin"
            />
            <span v-else class="i-lucide-sparkles size-5" />
            {{ isGenerating ? 'Gerando...' : 'Gerar Prompt' }}
          </button>
        </div>
      </div>

      <!-- Prompt Editor -->
      <div v-else class="bg-n-solid-3 rounded-xl p-6">
        <div class="flex items-center justify-between mb-4">
          <h3
            class="text-lg font-semibold text-n-slate-12 flex items-center gap-2"
          >
            <span class="i-lucide-message-square size-5 text-woot-500" />
            {{ t('LIA_AGENTS.PROMPT.EDITOR_TITLE') }}
          </h3>
          <div class="flex items-center gap-3">
            <span
              class="text-xs"
              :class="
                isOverLimit
                  ? 'text-red-500 font-semibold'
                  : charCount > maxChars
                    ? 'text-amber-500'
                    : 'text-n-slate-11'
              "
            >
              {{ charCount }}/{{ maxChars }}
            </span>
            <button
              type="button"
              class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              :disabled="isGenerating"
              @click="generateSinglePrompt"
            >
              <span
                :class="
                  isGenerating
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-refresh-cw'
                "
                class="size-3.5"
              />
              Regenerar
            </button>
            <button
              type="button"
              class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
              :class="
                showAssistant
                  ? 'bg-woot-500 text-white'
                  : 'bg-woot-50 text-woot-600 dark:bg-woot-900/30 dark:text-woot-400 hover:bg-woot-100'
              "
              @click="toggleAssistant"
            >
              <span class="i-lucide-sparkles size-4" />
              {{ t('LIA_AGENTS.PROMPT.ASSISTANT_BUTTON') }}
            </button>
            <button
              type="button"
              class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
              :disabled="isSaving || isSinglePromptEmpty || isOverLimit"
              @click="savePrompts"
            >
              <span
                :class="
                  isSaving ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-save'
                "
                class="size-4"
              />
              {{ isSaving ? 'Salvando...' : 'Salvar Prompt' }}
            </button>
          </div>
          <div
            v-if="saveMessage"
            class="flex items-center gap-1.5 text-xs font-medium mt-2"
            :class="saveSuccess ? 'text-green-600' : 'text-red-500'"
          >
            <span
              :class="
                saveSuccess ? 'i-lucide-check-circle' : 'i-lucide-alert-circle'
              "
              class="size-3.5"
            />
            {{ saveMessage }}
          </div>
        </div>

        <div class="flex gap-4">
          <textarea
            v-model="singlePrompt"
            rows="24"
            :placeholder="t('LIA_AGENTS.PROMPT.EDITOR_PLACEHOLDER')"
            class="flex-1 px-4 py-3 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500 font-mono text-sm resize-y min-h-[450px]"
          />

          <!-- AI Assistant Panel for single prompt -->
          <div
            v-if="showAssistant"
            class="w-80 bg-n-solid-2 rounded-xl flex flex-col overflow-hidden border border-n-weak"
          >
            <div
              class="px-4 py-3 border-b border-n-weak bg-gradient-to-r from-woot-500 to-purple-500"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="i-lucide-sparkles size-5 text-white" />
                  <h4 class="text-sm font-semibold text-white">
                    {{ t('LIA_AGENTS.PROMPT.ASSISTANT_TITLE') }}
                  </h4>
                </div>
                <button
                  type="button"
                  class="p-1 text-white/80 hover:text-white"
                  @click="showAssistant = false"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>
            </div>
            <div class="px-3 py-2 border-b border-n-weak">
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="suggestion in quickSuggestions"
                  :key="suggestion.id"
                  type="button"
                  class="px-2 py-1 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11 hover:bg-woot-100 hover:text-woot-600 transition-colors"
                  :disabled="isAssistantLoading"
                  @click="handleQuickSuggestion(suggestion)"
                >
                  {{ suggestion.label }}
                </button>
              </div>
            </div>
            <div
              ref="messagesContainer"
              class="flex-1 overflow-y-auto p-3 space-y-3"
            >
              <div
                v-for="(message, index) in assistantMessages"
                :key="index"
                class="flex"
                :class="
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                "
              >
                <div
                  class="max-w-[90%] rounded-lg px-3 py-2 text-sm"
                  :class="
                    message.role === 'user'
                      ? 'bg-woot-500 text-white'
                      : 'bg-n-alpha-2 text-n-slate-12'
                  "
                >
                  <p class="whitespace-pre-wrap">{{ message.content }}</p>
                  <button
                    v-if="message.improved_prompt"
                    type="button"
                    class="mt-2 w-full flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium rounded bg-green-500 text-white hover:bg-green-600"
                    @click="applyImprovedPrompt(message.improved_prompt)"
                  >
                    <span class="i-lucide-check size-3.5" />
                    {{ t('LIA_AGENTS.PROMPT.ASSISTANT_APPLY') }}
                  </button>
                </div>
              </div>
              <div v-if="isAssistantLoading" class="flex justify-start">
                <div class="bg-n-alpha-2 rounded-lg px-3 py-2">
                  <div class="flex items-center gap-1">
                    <span
                      class="w-2 h-2 bg-n-slate-11 rounded-full animate-bounce"
                      style="animation-delay: 0ms"
                    />
                    <span
                      class="w-2 h-2 bg-n-slate-11 rounded-full animate-bounce"
                      style="animation-delay: 150ms"
                    />
                    <span
                      class="w-2 h-2 bg-n-slate-11 rounded-full animate-bounce"
                      style="animation-delay: 300ms"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="p-3 border-t border-n-weak">
              <div class="flex gap-2">
                <input
                  v-model="assistantInput"
                  type="text"
                  :placeholder="t('LIA_AGENTS.PROMPT.ASSISTANT_PLACEHOLDER')"
                  class="flex-1 px-3 py-2 text-sm bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500"
                  :disabled="isAssistantLoading"
                  @keydown.enter="sendToAssistant()"
                />
                <button
                  type="button"
                  class="p-2 bg-woot-500 text-white rounded-lg hover:bg-woot-600 disabled:opacity-50"
                  :disabled="!assistantInput.trim() || isAssistantLoading"
                  @click="sendToAssistant()"
                >
                  <span class="i-lucide-send size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p
            class="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2"
          >
            <span class="i-lucide-lightbulb size-4 flex-shrink-0 mt-0.5" />
            <span>{{ t('LIA_AGENTS.PROMPT.TIP') }}</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
